/* eslint-disable react/no-unescaped-entities */

/* Bol Dashboard — toplam satis ozeti + gun/ay grafikleri + top urunler. */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    TrendingUp, ShoppingBag, Package, FileCheck, Loader2, Truck,
    Percent, Receipt, Wallet, AlertCircle, Megaphone,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmt = (v) => (Number(v) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (v) => (Number(v) || 0).toLocaleString('de-DE');

function KpiCard({ icon: Icon, label, value, sub, tone = 'gray', testId }) {
    const toneCls = {
        emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        sky: 'bg-sky-50 text-sky-700 border-sky-200',
        amber: 'bg-amber-50 text-amber-700 border-amber-200',
        violet: 'bg-violet-50 text-violet-700 border-violet-200',
        gray: 'bg-gray-50 text-gray-700 border-gray-200',
    }[tone];
    return (
        <div className={`border rounded-xl p-4 ${toneCls}`} data-testid={testId}>
            <div className="flex items-center gap-2 mb-1">
                <Icon size={14} />
                <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{value}</div>
            {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
        </div>
    );
}

function MiniBarChart({ data, valueKey = 'revenue', labelKey = 'date', testId, showDayName = false }) {
    if (!data || data.length === 0) {
        return <div className="text-center text-gray-400 py-8 text-sm">Henuz veri yok</div>;
    }
    const max = Math.max(...data.map((d) => d[valueKey] || 0), 1);
    // Iter 113.11: Ekrana sigsin — dinamik sutun, overflow yok
    const cellPct = `${(100 / data.length).toFixed(4)}%`;
    return (
        <div className="w-full" data-testid={testId}>
            <table className="border-collapse w-full" style={{ tableLayout: 'fixed' }}>
                <tbody>
                    {/* Row 1: sip count */}
                    <tr>
                        {data.map((d, i) => (
                            <td key={i} className="text-center align-bottom px-px" style={{ width: cellPct }}>
                                {d.orders > 0 && (
                                    <div className="text-[10px] font-bold text-gray-500 mb-1">{d.orders}</div>
                                )}
                            </td>
                        ))}
                    </tr>
                    {/* Row 2: bars */}
                    <tr>
                        {data.map((d, i) => {
                            const v = d[valueKey] || 0;
                            const h = max > 0 ? (v / max) * 100 : 0;
                            return (
                                <td key={i} className="align-bottom px-px" style={{ width: cellPct, height: '340px' }}>
                                    <div className="w-full bg-gray-100 rounded-t relative group" style={{ height: '340px' }}>
                                        <div className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t transition-all hover:from-blue-700 hover:to-blue-500 absolute bottom-0 left-0 right-0"
                                            style={{ height: `${h}%`, minHeight: v > 0 ? '4px' : '0' }}
                                            title={`${fmt(v)} € · ${d.orders} sip.`} />
                                        <div className="absolute left-1/2 -translate-x-1/2 -top-6 text-[10px] font-bold text-blue-700 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity bg-white px-1.5 py-0.5 rounded shadow-sm border border-blue-200 z-10">
                                            {fmt(v)} €
                                        </div>
                                    </div>
                                </td>
                            );
                        })}
                    </tr>
                    {/* Row 3: x-axis labels */}
                    <tr>
                        {data.map((d, i) => (
                            <td key={i} className="text-center pt-2 align-top px-px" style={{ width: cellPct, height: showDayName ? '48px' : '64px' }}>
                                {showDayName ? (
                                    <div className="flex flex-col items-center gap-0.5">
                                        <div className="text-[11px] font-bold text-gray-700">{d.day || ''}</div>
                                        <div className="text-[9px] text-gray-500 font-mono">{String(d[labelKey] || '').slice(5)}</div>
                                    </div>
                                ) : (
                                    <div className="text-[9px] text-gray-600 font-mono inline-block"
                                        style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                                        {labelKey === 'date' ? String(d[labelKey] || '').slice(5) : d[labelKey]}
                                    </div>
                                )}
                            </td>
                        ))}
                    </tr>
                </tbody>
            </table>
            <div className="text-[10px] text-gray-400 mt-2 flex items-center gap-3 flex-wrap">
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-blue-500 rounded" /> Ciro (€)</span>
                <span>· Bar üstündeki sayı: sipariş adedi</span>
                <span>· Hover → toplam tutar</span>
            </div>
        </div>
    );
}

export default function BolDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('weekly');  // Iter 113.20: default haftalık
    const [period, setPeriod] = useState('30d');  // Iter 110.2: default son 30 gün
    const [chartMonth, setChartMonth] = useState('');  // Iter 113.20: günlük için ay seçimi

    useEffect(() => {
        setLoading(true);
        const params = { period };
        if (view === 'daily' && chartMonth) params.chart_month = chartMonth;
        axios.get(`${API}/api/bol/dashboard`, { headers: hdrs(), params })
            .then((r) => setData(r.data))
            .catch(() => { /* ignore */ })
            .finally(() => setLoading(false));
    }, [period, view, chartMonth]);

    if (loading) {
        return <div className="p-12 text-center text-gray-400">
            <Loader2 size={20} className="animate-spin mx-auto" />
        </div>;
    }
    if (!data) {
        return <div className="p-12 text-center text-gray-400 text-sm">Veri yuklenemedi</div>;
    }

    // Iter 113.21: Backend redeploy edilmemişse fallback — daily'den haftalık + ay listesi türet
    const _dayNamesTR = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt']; // JS Date.getDay(): 0=Sun
    const weeklyData = (data.weekly && data.weekly.length > 0)
        ? data.weekly
        : (() => {
            // Son 7 günü daily array'inden türet (yoksa 0 ile doldur)
            const map = {};
            (data.daily || []).forEach((d) => { map[d.date] = d; });
            const out = [];
            const today = new Date();
            for (let i = 6; i >= 0; i--) {
                const dt = new Date(today);
                dt.setDate(today.getDate() - i);
                const ds = dt.toISOString().slice(0, 10);
                const row = map[ds];
                out.push({
                    date: ds,
                    day: _dayNamesTR[dt.getDay()],
                    revenue: row ? row.revenue : 0,
                    orders: row ? row.orders : 0,
                });
            }
            return out;
        })();
    const availableMonths = (data.available_months && data.available_months.length > 0)
        ? data.available_months
        : (() => {
            // daily + monthly verisinden türet
            const set = new Set();
            (data.daily || []).forEach((d) => { if (d.date) set.add(d.date.slice(0, 7)); });
            (data.monthly || []).forEach((m) => { if (m.month) set.add(m.month); });
            return Array.from(set).sort().reverse();
        })();

    const kpi = data.kpi || {};
    const costs = data.bol_costs || {};
    const ads = data.bol_ads || {};
    const hasInvoices = (costs.invoices_count || 0) > 0;
    const hasEstimate = (costs.commission_total || 0) > 0 || (costs.commission_estimated || 0) > 0;
    const hasAds = (ads.spent || 0) > 0 || (ads.impressions || 0) > 0;
    const totalCommission = costs.commission_total || 0;
    const totalTransport = costs.transport_total || 0;
    // Iter 110.2: Reklam payı yalnızca period="30d" ise net'ten düşülür
    //   (ads sadece 30 günlük döner; "tüm zaman" seçilince ad period eşleşmiyor)
    const adAppliesToPeriod = !!ads.applies_to_period;
    const adSpent = adAppliesToPeriod ? (ads.spent || 0) : 0;
    const netEarnings = (kpi.revenue_excl_vat || 0) - totalCommission - totalTransport - adSpent;

    return (
        <div className="space-y-4" data-testid="bol-dashboard">
            {/* Iter 110.2: Period Switch */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm font-semibold text-gray-700">
                    {data.period_label || (period === '30d' ? 'Son 30 gün' : 'Tüm zaman')}
                </div>
                <div className="inline-flex bg-gray-100 rounded-lg p-0.5" data-testid="bol-dashboard-period-switch">
                    <button
                        onClick={() => setPeriod('30d')}
                        data-testid="period-30d"
                        className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                            period === '30d' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                        }`}>
                        Son 30 gün
                    </button>
                    <button
                        onClick={() => setPeriod('all')}
                        data-testid="period-all"
                        className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                            period === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                        }`}>
                        Tüm zaman
                    </button>
                </div>
            </div>

            {/* KPI'lar — Satislar */}
            <div className="text-[11px] uppercase tracking-wider font-bold text-gray-500 px-1">Satışlar (Müşterilere)</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <KpiCard icon={TrendingUp} label="Toplam Ciro (BTW dahil)" tone="emerald"
                    value={`${fmt(kpi.total_revenue)} €`}
                    sub={`${kpi.total_orders} sipariş`}
                    testId="kpi-revenue" />
                <KpiCard icon={Wallet} label="Net Ciro (BTW hariç)" tone="emerald"
                    value={`${fmt(kpi.revenue_excl_vat)} €`}
                    sub="vergi öncesi"
                    testId="kpi-revenue-net" />
                <KpiCard icon={Percent} label="BTW 21% (KDV)" tone="violet"
                    value={`${fmt(kpi.btw_21)} €`}
                    sub="müşterilerden tahsil"
                    testId="kpi-btw" />
                <KpiCard icon={ShoppingBag} label="Toplam Sipariş" tone="sky"
                    value={fmtInt(kpi.total_orders)}
                    testId="kpi-total-orders" />
                <KpiCard icon={Package} label="Satılan Ürün" tone="violet"
                    value={fmtInt(kpi.total_items)}
                    sub="toplam adet"
                    testId="kpi-items" />
                <KpiCard icon={FileCheck} label="Billit Faturası" tone="emerald"
                    value={fmtInt(kpi.billit_pushed)}
                    sub={`/${kpi.total_orders} oluşturuldu`}
                    testId="kpi-billit" />
            </div>

            {/* KPI'lar — Kargo Durumu */}
            <div className="text-[11px] uppercase tracking-wider font-bold text-gray-500 px-1 pt-2">Kargo Durumu</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <KpiCard icon={Truck} label="Gönderilecek" tone="amber"
                    value={fmtInt(kpi.open_orders)}
                    sub="bekleyen sipariş"
                    testId="kpi-open" />
                <KpiCard icon={Truck} label="Gönderildi" tone="sky"
                    value={fmtInt(kpi.shipped_orders)}
                    sub="kargoda / teslim edildi"
                    testId="kpi-shipped" />
                <KpiCard icon={Package} label="Teslim Oranı" tone="emerald"
                    value={`${kpi.total_orders ? Math.round((kpi.shipped_orders / kpi.total_orders) * 100) : 0}%`}
                    sub={`${kpi.shipped_orders}/${kpi.total_orders}`}
                    testId="kpi-delivery-rate" />
            </div>

            {/* KPI'lar — Bol Maliyetleri */}
            <div className="text-[11px] uppercase tracking-wider font-bold text-gray-500 px-1 pt-2 flex items-center gap-2">
                Bol.com Maliyetleri (canlı tahmin — tüm siparişler için)
                {!hasEstimate && (
                    <span className="ml-1 text-amber-700 normal-case font-normal text-[11px] flex items-center gap-1">
                        <AlertCircle size={11} /> Henüz sync edilmedi — "Bol.com'dan Çek" butonuna basın
                    </span>
                )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard icon={Receipt} label="Bol Komisyonu" tone={hasEstimate ? 'rose' : 'gray'}
                    value={`${fmt(costs.commission_total)} €`}
                    sub={
                        costs.commission_source === 'actual' ? `${kpi.total_orders} sipariş · Bol fatura (gerçek)` :
                        costs.commission_source === 'mixed' ? `${costs.orders_with_actual_commission} gerçek · ${kpi.total_orders - costs.orders_with_actual_commission} tahmini` :
                        `${kpi.total_orders} sipariş · tahmini`
                    }
                    testId="kpi-bol-commission" />
                <KpiCard icon={Truck} label="Kargo Ücreti" tone={hasEstimate ? 'amber' : 'gray'}
                    value={`${fmt(costs.transport_total)} €`}
                    sub={
                        costs.transport_source === 'actual' ? 'Bol fatura (gerçek)' :
                        costs.transport_source === 'mixed' ? `${costs.orders_with_actual_shipping} gerçek · kalanlar tahmini` :
                        'gönderilmiş · tahmini'
                    }
                    testId="kpi-bol-transport" />
                <KpiCard icon={Megaphone} label="Reklam Maliyeti" tone={hasAds ? 'rose' : 'gray'}
                    value={`${fmt(ads.spent)} €`}
                    sub={hasAds
                        ? (adAppliesToPeriod ? 'son 30 gün · net\'ten düşüldü' : 'son 30 gün · "tüm zaman"a düşülmedi')
                        : 'Advertising scope yetkisi eksik'}
                    testId="kpi-ad-spent" />
                <KpiCard icon={Wallet} label="Net Kazanç" tone={hasEstimate ? 'emerald' : 'gray'}
                    value={`${fmt(netEarnings)} €`}
                    sub={adAppliesToPeriod
                        ? `BTW hariç − komisyon − kargo − reklam${costs.commission_source === 'mixed' ? ' (kısmi tahmini)' : ''}`
                        : `BTW hariç − komisyon − kargo (reklam dahil değil)`}
                    testId="kpi-net-earnings" />
            </div>

            {/* Chart */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Satislar</h3>
                    <div className="flex items-center gap-2 flex-wrap">
                        {view === 'daily' && availableMonths.length > 0 && (
                            <select
                                value={chartMonth}
                                onChange={(e) => setChartMonth(e.target.value)}
                                data-testid="chart-month-select"
                                className="text-xs border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                            >
                                <option value="">Son 60 gün</option>
                                {availableMonths.map((m) => {
                                    const [y, mo] = m.split('-');
                                    const monthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
                                    const label = `${monthNames[parseInt(mo, 10) - 1] || mo} ${y}`;
                                    return <option key={m} value={m}>{label}</option>;
                                })}
                            </select>
                        )}
                        <div className="flex gap-1 text-xs">
                            {[
                                { k: 'weekly', l: 'Haftalık' },
                                { k: 'daily', l: 'Günlük' },
                                { k: 'monthly', l: 'Aylık' },
                            ].map((v) => (
                                <button key={v.k} onClick={() => setView(v.k)}
                                    className={`px-3 py-1 rounded ${view === v.k ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                    data-testid={`view-${v.k}`}>
                                    {v.l}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                {view === 'weekly' && <MiniBarChart data={weeklyData} labelKey="date" testId="chart-weekly" showDayName />}
                {view === 'daily' && <MiniBarChart data={data.daily} labelKey="date" testId="chart-daily" />}
                {view === 'monthly' && <MiniBarChart data={data.monthly} labelKey="month" testId="chart-monthly" />}
            </div>

            {/* Top urunler */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">En Cok Satan 10 Urun</h3>
                {(data.top_products || []).length === 0 ? (
                    <div className="text-center text-gray-400 py-8 text-sm">Veri yok</div>
                ) : (
                    <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[400px]">
                        <thead>
                            <tr className="text-xs text-gray-500 border-b">
                                <th className="text-left p-2">Urun</th>
                                <th className="text-right p-2 whitespace-nowrap">Adet</th>
                                <th className="text-right p-2 whitespace-nowrap">Ciro</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {data.top_products.map((p, i) => (
                                <tr key={i} data-testid={`top-product-${i}`}>
                                    <td className="p-2 text-gray-900 max-w-[200px] truncate" title={p.title}>{p.title}</td>
                                    <td className="p-2 text-right whitespace-nowrap">{p.qty}</td>
                                    <td className="p-2 text-right font-semibold whitespace-nowrap">{fmt(p.revenue)} €</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                )}
            </div>
        </div>
    );
}
